import { push } from "react-router-redux";
import { datadogRum } from '@datadog/browser-rum';

import * as types from "./actionTypes";
import * as apiUrls from "./apiUrls";
import { BaseApi } from "../shared/api";
import { segmentEventTracker } from "../../utils/SegmentEventTracker";

import {
  sessionExpire,
  notify
} from "../shared/actions";
import { fetchCompany, hydrateCompany } from "../company/actions";
import { checkUnreadNotifications } from "../notifications/actions";
import {fetchBusinessAccountState} from "../business_account/actions";
import {isNil} from 'lodash'

import Cookies from "universal-cookie";

const cookies = new Cookies();

export function userValidating() {
  return { type: types.USER_AUTH_VALIDATING };
}

export function userDoneValidating() {
  return { type: types.USER_AUTH_DONE_VALIDATING };
}

function userAcceptToc() {
  return { type: types.USER_ACCEPT_TOC };
}

export function clearAuthCookies() {
  cookies.remove("jwtToken", { path: "/", domain: process.env.REACT_APP_FCA_DOMAIN });
  // NOTE: Cookie removed below is for deployment previews
  cookies.remove("jwtToken", { path: "/" });
}

export function clearSessionId() {
  cookies.remove("sessionId", { path: "/", domain: process.env.REACT_APP_FCA_DOMAIN });
  // NOTE: Cookie removed below is for deployment previews
  cookies.remove("sessionId", { path: "/" });
}

function redirectAction(dispatch) {
  // TODO: Update this bank auto redirection as needed.
  // We now disabled bank enabled customer with consent
  // accessing FCA pages except /draw_down page
  // until such time we build a new draw_down screen on payments app see action.

  const callback = () => {
    dispatch(userDoneValidating());
  };

  const path = window.location.pathname;

  if (path === "/draw_down") {
    dispatch(fetchBusinessAccountState(false, callback, callback));
  } else {
    dispatch(fetchBusinessAccountState(true, callback, callback));
  }
}

export function setSessionId(sessionId) {
  if (isNil(sessionId)) { return }
  clearSessionId();

  if (process.env.REACT_APP_FCA_DOMAIN) {
    cookies.set("sessionId", sessionId, { path: "/", domain: process.env.REACT_APP_FCA_DOMAIN });
  }

  // NOTE: Cookies set with custom domain are rejected on netlify previews
  // so we set a fallback cookie if it's not set yet with no domain option
  if (cookies.get('sessionId')) { return }

  cookies.set("sessionId", sessionId, { path: "/" });
}

export function setAuthCookies(token) {
  clearAuthCookies();

  if (process.env.REACT_APP_FCA_DOMAIN) {
    cookies.set("jwtToken", token, { path: "/", domain: process.env.REACT_APP_FCA_DOMAIN });
  }

  // NOTE: Cookies set with custom domain are rejected on netlify previews
  // so we set a fallback cookie if it's not set yet with no domain option
  if (cookies.get('jwtToken')) { return }

  cookies.set("jwtToken", token, { path: "/" });
}

function receiveDetailsAndToken(json) {
  setAuthCookies(json.auth_token);
  setSessionId(json.session_id);
  cookies.set("userId", json.user.id, { path: "/" });
  cookies.set("companyId", json.user.company_id, { path: "/" });

  // Track authenticated user on DataDog RUM
  datadogRum.setUser({
    id: json.user.id,
    name: `${json.user.first_name} ${json.user.last_name}`,
    email: json.user.email,
    companyId: json.user.company_id
  })

  return {
    type: types.USER_RECEIVE_DETAILS_AND_TOKEN,
    payload: {
      auth_token: json.auth_token,
      id: json.user.id,
      email: json.user.email,
      new_email_temp: json.user.new_email_temp,
      last_name: json.user.last_name,
      first_name: json.user.first_name,
      profile_photo: json.user.profile_photo,
      phones: json.user.phones,
      company_slug: json.user.default_company_slug,
      trustpasses_incomplete: json.user.trustpasses_incomplete,
      active_business_trustpass: json.user.active_business_trustpass,
      active_personal_trustpass: json.user.active_personal_trustpass,
      kyc_verified: json.user.kyc_verified,
      kyc_verified_at_least_once: json.user.kyc_verified_at_least_once,
      bank_accounts: json.user.bank_accounts,
      has_current_terms_accepted: json.user.has_current_terms_accepted,
      has_trade_documents: json.user.has_trade_documents,
      loan_application_id: json.user.loan_application_id,
      user_active: json.user.user_active,
      accepted_loan_offers_count: json.user.accepted_loan_offers_count,
      has_loan_offers_for_acceptance: json.user.has_loan_offers_for_acceptance,
      onboarding: json.user.onboarding,
      onboarding_finished: json.user.onboarding_finished,
      role: json.user.role,
      is_sales_qualified: json.user.is_sales_qualified,
      is_not_sales_qualified: json.user.is_not_sales_qualified,
      is_from_csv: json.user.is_from_csv,
    }
  };
}

export function userAcceptTermsAndConditions(userId, onSuccess) {
  return (dispatch) => {
    return fetch(apiUrls.apiV2TermsAcceptance(userId), {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        'Authorization': cookies.get('jwtToken'),
      }),
    })
      .then(response => {
        if (response.ok) {
          dispatch(userAcceptToc());
          onSuccess();
        }
      })
  };
}

export function userForgotPassword(body) {
  return (dispatch) => {
    return fetch(apiUrls.apiV2UserForgotPasswordUrl(), {
      method: "POST",
      body: JSON.stringify({ user: body }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(response => {
        if (response.status === 500) {
          dispatch(notify({
            head: "",
            body: `Error! Something went wrong. Please try again.`,
            duration: 3,
            className: "-danger"
          }))
        } else {
          dispatch(push('/email_sent', { email: body.email }))
          dispatch(notify({
            head: "",
            body: `Success! An email with instructions has been sent to: ${body.email}.`,
            duration: 3,
            className: "-success"
          }))
        }
      })
  };
}

export function loadUserFromToken() {
  const auth_token = cookies.get("jwtToken");
  const userId = cookies.get("userId");
  const companyId = cookies.get("companyId");

  return (dispatch) => {
    if (!auth_token) {
      dispatch({ type: "INVALID_AUTH_TOKEN" });
      dispatch(signOut());
      return;
    }

    dispatch(hydrateUser({ id: userId, auth_token: auth_token }));
    dispatch(hydrateCompany({ id: companyId }));
    dispatch(userValidating());

    return fetch(apiUrls.apiV2ValidateAuthTokenUrl(), {
      method: "POST",
      body: JSON.stringify({ auth_token: auth_token }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(response => response.json())
      .then(json => {
        if (!!json.validated) {
          dispatch(receiveDetailsAndToken({ auth_token, user: json.user }));

          segmentEventTracker.identify(json.user.id)

          dispatch(
            fetchCompany(
              () => {
                dispatch(checkUnreadNotifications());
              }
            )
          );

          redirectAction(dispatch);

        } else {
          dispatch(userDoneValidating());
          dispatch(sessionExpire());
        }
      })
      .catch(error => {
        dispatch({
          type: "HANDLE_ERRORS",
          payload: error,
        })
        dispatch({ type: "LOADING_STOP" });
      });
  }
}

export function updateUser(body, callback) {
  return (dispatch, getState) => {
    const { user } = getState();
    const api = new BaseApi(dispatch, getState);
    return api.putRequest(
      apiUrls.user(user.id),
      body,
      response => {
        dispatch(hydrateUser(body));
        callback(response);
      }
    )
  }
}

export function userLogin(email, password, onSuccess, onError) {
  return (dispatch) => {
    dispatch(userValidating());

    return fetch(apiUrls.apiV2AuthenticateUserUrl(), {
      method: "POST",
      body: JSON.stringify({ email, password }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(response => {
        response.json().then(json => {
          if (response.ok) {
            setAuthCookies(json.auth_token);
            setSessionId(json.session_id);
            cookies.set("userId", json.user.id, { path: "/" });
            cookies.set("userEmail", json.user.email, { path: "/" });
            cookies.set("companyId", json.user.company_id, { path: "/" });

            // This is very important and higly needed for segment tracking
            segmentEventTracker.identify(json.user.id);

            dispatch(receiveDetailsAndToken(json));

            dispatch(checkUnreadNotifications());
            dispatch(fetchCompany());
            const callback = () => {
              dispatch(userDoneValidating());
            };
            dispatch(fetchBusinessAccountState(true, callback, callback));
            onSuccess();
          } else {
            onError(json.error);
          }
        })
      })
      .catch(error => {
        dispatch({
          type: "HANDLE_ERRORS",
          payload: error,
        })
        dispatch({ type: "LOADING_STOP" });
      });
  };
}

export function hydrateUser(payload) {
  return {
    type: types.USER_HYDRATE,
    payload: payload
  };
}

export function signOut() {
  clearAuthCookies();
  clearSessionId();
  cookies.remove("userId", { path: "/" });
  cookies.remove("userEmail", { path: "/" });
  cookies.remove("companyId", { path: "/" });

  // Reset segment context
  segmentEventTracker.resetContext()

  // Remove user on RUM
  datadogRum.removeUser()

  return { type: types.USER_SIGN_OUT };
}

export function finishOnboarding() {
  return {
    type: types.FINISH_ONBOARDING,
  }
}

export function receiveNewEmail(json) {
  return {
    type: types.USER_RECEIVE_NEW_EMAIL,
    payload: {
      email: json.user.email,
      new_email_temp: json.user.new_email_temp,
    }
  }
}

function validateUserResetPasswordTokenStart() {
  return { type: types.VALIDATE_USER_RESET_PASSWORD_TOKEN_START };
}

function validateUserResetPasswordTokenDone() {
  return { type: types.VALIDATE_USER_RESET_PASSWORD_TOKEN_DONE };
}

function userResetPasswordTokenExpired() {
  return { type: types.USER_RESET_PASSWORD_TOKEN_EXPIRED };
}

export function validateUserResetPasswordToken(queryString) {
  return (dispatch) => {
    dispatch(validateUserResetPasswordTokenStart());
    const url = `${apiUrls.apiV2UserNewResetPasswordUrl()}?${queryString}`;

    // TODO:
    // I think this can be simplified by creating
    // an API class where it has http request methods
    // so we can avoid code repetition.
    return fetch(url, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(response => {
        if (response.ok) {
          dispatch(validateUserResetPasswordTokenDone());
        } else {
          dispatch(userResetPasswordTokenExpired());
        }
      })
      .catch(error => {
        dispatch({
          type: "HANDLE_ERRORS",
          payload: error,
        })
        dispatch({ type: "LOADING_STOP" });
      });
  };
}

function resetUserPasswordStart() {
  return { type: types.RESET_USER_PASSWORD_START };
}

export function resetUserPasswordDone() {
  return { type: types.RESET_USER_PASSWORD_DONE };
}

function resetUserPasswordFailed() {
  return { type: types.RESET_USER_PASSWORD_FAILED };
}

export function userResetPassword(body) {
  return (dispatch) => {
    dispatch(resetUserPasswordStart());
    return fetch(apiUrls.apiV2UserResetPasswordUrl(), {
      method: "POST",
      body: JSON.stringify({ user: body }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(response => response.json())
      .then(json => {
        setAuthCookies(json.auth_token);
        setSessionId(json.session_id);
        cookies.set("userId", json.user.id, { path: "/" });
        cookies.set("companyId", json.user.company_id, { path: "/" });

        dispatch(fetchCompany(
          () => {
            dispatch(receiveDetailsAndToken(json));
            dispatch(checkUnreadNotifications());

            const callback = () => {
              dispatch(userDoneValidating());
            };
            dispatch(fetchBusinessAccountState(true, callback, callback));
            dispatch(resetUserPasswordDone())
          })
        );
      })
      .catch(error => {
        dispatch({
          type: "HANDLE_ERRORS",
          payload: error,
        })
        dispatch({ type: "LOADING_STOP" });
        dispatch(resetUserPasswordFailed());
      });
  };
}



// WEBPACK FOOTER //
// ./src/features/user/actions.js